import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../hooks/use-toast";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  LogIn,
  Megaphone,
  Sprout,
  UserPlus,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChurchHubPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, token, refreshUser, setAuth } = useAuth();

  const [church, setChurch] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Join flow state
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinForm, setJoinForm] = useState({ name: "", email: "", password: "" });
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const c = await axios.get(`${API}/churches/by-slug/${slug}`);
        if (cancelled) return;
        setChurch(c.data);
        const a = await axios.get(`${API}/churches/${c.data.id}/announcements`);
        if (cancelled) return;
        setAnnouncements(a.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Hub not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const joinAsAuthed = async () => {
    if (!church || !token) return;
    setJoining(true);
    try {
      await axios.post(
        `${API}/churches/${slug}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      toast({
        title: "Welcome to the hub",
        description: `You're now a member of ${church.name}.`,
      });
    } catch (err) {
      toast({
        title: "Could not join",
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setJoining(false);
    }
  };

  const joinAsGuest = async (e) => {
    e.preventDefault();
    if (
      joinForm.name.length < 2 ||
      !joinForm.email.includes("@") ||
      joinForm.password.length < 8
    ) {
      toast({
        title: "Check your details",
        description: "Name 2+ chars, valid email, password 8+ chars.",
      });
      return;
    }
    setJoining(true);
    try {
      const res = await axios.post(
        `${API}/churches/${slug}/register-and-join`,
        joinForm
      );
      // Update auth state atomically (token + user) — refreshUser closure can see stale token.
      setAuth(res.data.token, res.data.user);
      toast({
        title: "Welcome to the hub",
        description: `You're now a member of ${church.name}.`,
      });
      setJoinOpen(false);
      setJoinForm({ name: "", email: "", password: "" });
    } catch (err) {
      toast({
        title: "Could not join",
        description:
          err?.response?.data?.detail ||
          "Please try again or sign in if you already have an account.",
      });
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen antialiased">
        <Navbar />
        <div className="pt-40 flex items-center justify-center text-white/60">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading hub…
        </div>
      </div>
    );
  }

  if (error || !church) {
    return (
      <div className="bg-black text-white min-h-screen antialiased">
        <Navbar />
        <section className="pt-40 pb-20 bg-[#04181a]">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Hub not found
            </h1>
            <p className="mt-3 text-white/55">
              We couldn&apos;t find a church hub at <code>/c/{slug}</code>.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1.5 text-teal-300 hover:text-teal-200"
            >
              <ArrowLeft className="w-4 h-4" /> Back home
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const accent = church.accent_color || "#14b8a6";
  const isMemberHere = user && user.church_id === church.id;
  const isMemberElsewhere = user && user.church_id && user.church_id !== church.id;
  const isOwner = user && user.id === church.owner_id;

  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />

      {/* Hero */}
      <section
        className="pt-28 md:pt-36 pb-16"
        style={{
          background: `linear-gradient(180deg, #000 0%, ${accent}1a 50%, #000 100%)`,
        }}
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/55 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Good Fruit home
          </Link>
          {church.logo_url && (
            <div className="mt-6">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${church.logo_url}`}
                alt={`${church.name} logo`}
                data-testid="hub-logo"
                className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover border border-white/10 bg-black/40"
              />
            </div>
          )}
          <div
            className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs tracking-wide border"
            style={{
              backgroundColor: `${accent}1a`,
              borderColor: `${accent}66`,
              color: accent,
            }}
          >
            <Sprout className="w-3.5 h-3.5" />
            Church Hub · /c/{church.slug}
          </div>
          <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            {church.name}
          </h1>
          {church.description && (
            <p className="mt-4 text-white/70 text-lg max-w-2xl leading-relaxed">
              {church.description}
            </p>
          )}

          {/* Join CTA */}
          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="join-cta-block">
            {isMemberHere ? (
              <div
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm border"
                style={{
                  backgroundColor: `${accent}1a`,
                  borderColor: `${accent}66`,
                  color: accent,
                }}
                data-testid="already-member-badge"
              >
                <Check className="w-4 h-4" />
                {isOwner
                  ? "You own this hub"
                  : `You're a member of ${church.name}`}
              </div>
            ) : isMemberElsewhere ? (
              <div className="text-sm text-white/55" data-testid="member-elsewhere-notice">
                You already belong to another hub. Leave it first to join here.
              </div>
            ) : user ? (
              <Button
                onClick={joinAsAuthed}
                disabled={joining}
                data-testid="join-as-authed-button"
                className="font-semibold h-11 px-6 text-black hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: accent }}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {joining ? "Joining…" : `Join ${church.name}`}
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => setJoinOpen(true)}
                  data-testid="open-join-modal-button"
                  className="font-semibold h-11 px-6 text-black hover:opacity-90"
                  style={{ backgroundColor: accent }}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Join this hub
                </Button>
                <button
                  onClick={() => navigate("/login")}
                  data-testid="goto-login-link"
                  className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
                >
                  <LogIn className="w-4 h-4" /> Already have an account? Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Welcome */}
      {church.welcome_message && (
        <section className="bg-white text-black py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
              A note from your church
            </div>
            <div
              className="mt-4 text-xl md:text-2xl font-serif italic leading-relaxed whitespace-pre-wrap"
              style={{ color: "#111" }}
            >
              &ldquo;{church.welcome_message}&rdquo;
            </div>
          </div>
        </section>
      )}

      {/* Announcements */}
      <section className="bg-[#04181a] py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5" style={{ color: accent }} />
            <div
              className="text-xs uppercase tracking-[0.18em] font-medium"
              style={{ color: accent }}
            >
              Announcements
            </div>
          </div>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight">
            Updates from {church.name}
          </h2>

          <div className="mt-8 space-y-4">
            {announcements.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-white/50">
                No announcements yet. Check back soon.
              </div>
            ) : (
              announcements.map((a) => (
                <article
                  key={a.id}
                  className="rounded-xl border border-white/10 bg-white/[0.04] p-6"
                >
                  <h3 className="text-lg md:text-xl font-semibold tracking-tight text-white">
                    {a.title}
                  </h3>
                  <div className="mt-2 text-white/75 leading-relaxed whitespace-pre-wrap">
                    {a.body}
                  </div>
                  <div className="mt-3 text-[11px] uppercase tracking-wide text-white/40">
                    {new Date(a.created_at).toLocaleDateString()} · {a.author_name}
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Join modal (guest) */}
      {joinOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setJoinOpen(false)}
          data-testid="join-modal-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-white/10 bg-[#04181a] p-6 md:p-8"
          >
            <div
              className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] tracking-wide border"
              style={{
                backgroundColor: `${accent}1a`,
                borderColor: `${accent}66`,
                color: accent,
              }}
            >
              <Sprout className="w-3 h-3" /> Join {church.name}
            </div>
            <h3 className="mt-3 text-2xl font-semibold tracking-tight">
              Create your account
            </h3>
            <p className="mt-1 text-sm text-white/55">
              Takes 30 seconds. You&apos;ll be added to this hub instantly.
            </p>

            <form onSubmit={joinAsGuest} className="mt-5 space-y-3">
              <div>
                <label className="block text-xs text-white/70 mb-1.5">
                  Your name
                </label>
                <Input
                  value={joinForm.name}
                  onChange={(e) =>
                    setJoinForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Jane Doe"
                  data-testid="join-name-input"
                  className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400"
                />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1.5">Email</label>
                <Input
                  type="email"
                  value={joinForm.email}
                  onChange={(e) =>
                    setJoinForm((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder="you@email.com"
                  data-testid="join-email-input"
                  className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400"
                />
              </div>
              <div>
                <label className="block text-xs text-white/70 mb-1.5">
                  Password
                </label>
                <Input
                  type="password"
                  value={joinForm.password}
                  onChange={(e) =>
                    setJoinForm((f) => ({ ...f, password: e.target.value }))
                  }
                  placeholder="At least 8 characters"
                  data-testid="join-password-input"
                  className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400"
                />
              </div>
              <Button
                type="submit"
                disabled={joining}
                data-testid="submit-join-button"
                className="w-full font-semibold h-11 text-black hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: accent }}
              >
                {joining ? "Joining…" : "Join hub"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <div className="text-xs text-white/50 text-center pt-1">
                Already have an account?{" "}
                <Link to="/login" className="text-teal-300 hover:text-teal-200">
                  Sign in instead
                </Link>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
      <Toaster />
    </div>
  );
};

export default ChurchHubPage;
